/**
 * ScrollTrigger reveal animations.
 *
 * Markup contract:
 *   .animation-container          → wrapper that scopes a group of reveals
 *     .animation-item-up          → rises into place (from below)
 *     .animation-item-down        → descends into place (from above)
 *     .animation-item-left        → slides leftward (from the right)
 *     .animation-item-right        → slides rightward (from the left)
 *
 * Items inside the same container are revealed with a stagger as they
 * enter the viewport. Respects `prefers-reduced-motion`.
 *
 * Relies on `gsap` + `ScrollTrigger` registered globally in index.js.
 */

const CONFIG = {
  distance: 60, // px offset, mirrors $animation_offset (6rem @ 10px root)
  duration: 0.9,
  stagger: 0.12,
  ease: "power3.out",
  start: "top 85%",
};

const DIRECTIONS = {
  up: { y: CONFIG.distance },
  down: { y: -CONFIG.distance },
  left: { x: CONFIG.distance },
  right: { x: -CONFIG.distance },
};

const animations = () => {
  const containers = document.querySelectorAll(".animation-container");
  if (!containers.length) return;

  // No-motion users keep the static, fully-visible layout.
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  // Enables the SCSS hidden/offset resting state and avoids FOUC.
  document.documentElement.classList.add("has-animations");

  Object.entries(DIRECTIONS).forEach(([direction, offset]) => {
    const items = gsap.utils.toArray(
      `.animation-container .animation-item-${direction}`
    );
    if (!items.length) return;

    gsap.set(items, { autoAlpha: 0, ...offset });

    ScrollTrigger.batch(items, {
      start: CONFIG.start,
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, {
          autoAlpha: 1,
          x: 0,
          y: 0,
          duration: CONFIG.duration,
          ease: CONFIG.ease,
          stagger: CONFIG.stagger,
          overwrite: true,
        }),
    });
  });
};

/**
 * Reveal `.animation-item-*` inside a freshly-injected `root` (e.g. AJAX
 * content). The page-load batch only knows about nodes present at init, so
 * dynamically added items keep the SCSS hidden state and never appear.
 * No-op when animations are inactive (reduced-motion / no JS reveal).
 */
export const revealWithin = (root) => {
  if (!root || !document.documentElement.classList.contains("has-animations")) {
    return;
  }

  Object.entries(DIRECTIONS).forEach(([direction, offset]) => {
    const items = gsap.utils.toArray(
      root.querySelectorAll(`.animation-item-${direction}`)
    );
    if (!items.length) return;

    gsap.fromTo(
      items,
      { autoAlpha: 0, ...offset },
      {
        autoAlpha: 1,
        x: 0,
        y: 0,
        duration: CONFIG.duration,
        ease: CONFIG.ease,
        stagger: CONFIG.stagger,
        overwrite: true,
      }
    );
  });
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", animations);
} else {
  animations();
}

export default animations;
